import React, { useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import {
  DAY_FLAG_GROUPS,
  DAY_OPERATORIES,
  DAY_QUICK_FILTERS,
  DAY_STATUS_AXES,
  type DayOptionsCheckItem,
} from '../../calendar/dayOptionsData';
import type { CalendarTimeDensity, CalendarViewMode } from '../../calendar/types';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { ViewportDrawer } from '../Overlay/ViewportDrawer';

const eyebrowClass =
  'font-[var(--font-mono)] text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink-3)]';

export interface CalendarDayOptionsDrawerProps {
  open: boolean;
  onClose: () => void;
  quick: string;
  onQuickChange: (v: string) => void;
  large: boolean;
  onLargeChange: (v: boolean) => void;
  viewMode: CalendarViewMode;
  onViewModeChange: (mode: CalendarViewMode) => void;
  timeDensity: CalendarTimeDensity;
  onTimeDensityChange: (density: CalendarTimeDensity) => void;
  productionThreshold: number;
  onProductionThresholdChange: (value: number) => void;
  /** Real provider columns for the current day (Provider view). */
  providers?: ReadonlyArray<{ id: string; name: string; role: string }>;
  /** Provider column ids the user has hidden. */
  hiddenProviders?: ReadonlySet<string>;
  onToggleProvider?: (id: string) => void;
  onResetProviders?: () => void;
  /** Status ids the user has un-ticked (booking / confirmation / visit axes). */
  hiddenStatuses?: ReadonlySet<string>;
  onToggleStatus?: (id: string) => void;
  onResetStatus?: () => void;
  /** Real operatory columns for the current day. Falls back to the static sample list when omitted. */
  operatories?: ReadonlyArray<{ id: string; name: string; role?: string }>;
  hiddenOperatories?: ReadonlySet<string>;
  onToggleOperatory?: (id: string) => void;
  onResetOperatories?: () => void;
  /** Ticked flags — appointments must carry at least one. */
  selectedFlags?: ReadonlySet<string>;
  onToggleFlag?: (id: string) => void;
  onResetFlags?: () => void;
  /** "Showing X of Y" for the current filters. */
  resultSummary?: { matched: number; total: number };
  onReset: () => void;
}

export function CalendarDayOptionsDrawer({
  open,
  onClose,
  quick,
  onQuickChange,
  large,
  onLargeChange,
  viewMode,
  onViewModeChange,
  timeDensity,
  onTimeDensityChange,
  productionThreshold,
  onProductionThresholdChange,
  providers = [],
  hiddenProviders,
  onToggleProvider,
  onResetProviders,
  hiddenStatuses,
  onToggleStatus,
  onResetStatus,
  operatories,
  hiddenOperatories,
  onToggleOperatory,
  onResetOperatories,
  selectedFlags,
  onToggleFlag,
  onResetFlags,
  resultSummary,
  onReset,
}: CalendarDayOptionsDrawerProps) {
  const operatoryRows = operatories ?? DAY_OPERATORIES.map((o) => ({
    id: o.id,
    name: o.label,
    ...(o.sublabel ? { role: o.sublabel } : {}),
  }));
  const statusCount = DAY_STATUS_AXES.reduce((n, axis) => n + axis.items.length, 0);
  const flagCount = DAY_FLAG_GROUPS.reduce((n, group) => n + group.items.length, 0);

  const timeDensityHint =
    timeDensity === 'Small'
      ? '1 hour intervals'
      : timeDensity === 'Medium'
        ? '30 minute intervals'
        : '15 minute intervals';

  return (
    <ViewportDrawer open={open} onClose={onClose} width="min(640px, 94vw)">
      <div
        className="flex h-full flex-col"
        style={{ background: '#F1F4F8' }}
      >
        <div className="flex items-center gap-2.5 border-b border-[var(--cream-2)] px-5 py-4">
          <Icon kind="filter" size={14} color="var(--ink-3)" />
          <span className="font-[var(--font-display)] text-[15px] font-semibold tracking-[-0.015em] text-[var(--ink-0)]">
            Options
          </span>
          {resultSummary ? (
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">
              {resultSummary.matched} of {resultSummary.total}
            </span>
          ) : null}
          <span className="ml-auto">
            <IconButton icon="x" size={30} onClick={onClose} label="Close" />
          </span>
        </div>

        <div className="cal-scroll flex-1 overflow-y-auto px-5 py-4 pb-6">
          <div className="grid grid-cols-1 gap-x-5 sm:grid-cols-2">
            <div>
              <OptionsSection title="View mode">
                <ToggleRow
                  options={['Provider', 'Operatory']}
                  value={viewMode}
                  onChange={(v) => onViewModeChange(v as CalendarViewMode)}
                />
              </OptionsSection>

              <OptionsSection title="Quick filters">
                <div className="flex flex-wrap gap-1.5">
                  {DAY_QUICK_FILTERS.map((q) => (
                    <QuickFilterChip
                      key={q.id}
                      active={quick === q.id}
                      onClick={() => onQuickChange(quick === q.id ? 'all' : q.id)}
                      color={q.color}
                      tint={q.tint}
                    >
                      {q.label}
                    </QuickFilterChip>
                  ))}
                </div>
              </OptionsSection>

              <FilterCategory
                title="Appointment status"
                count={String(statusCount)}
                onReset={() => onResetStatus?.()}
              >
                {DAY_STATUS_AXES.map((axis, ai) => (
                  <div key={axis.key} className={ai > 0 ? 'mt-2.5' : undefined}>
                    <div className="px-1 pb-1 pt-0.5">
                      <span className="text-[11px] font-semibold tracking-[-0.005em] text-[var(--ink-1)]">
                        {axis.label}
                      </span>
                      <span className="ml-1 text-[10.5px] text-[var(--ink-4)]">· {axis.hint}</span>
                    </div>
                    {axis.note ? (
                      <p className="px-1 pb-1 text-[10px] text-[var(--ink-4)]">{axis.note}</p>
                    ) : null}
                    {axis.items.map((s) => (
                      <OptionsCheckRow
                        key={s.id}
                        label={s.label}
                        {...(s.dot ? { dot: s.dot } : {})}
                        {...(onToggleStatus
                          ? {
                              checked: !hiddenStatuses?.has(s.id),
                              onChange: () => onToggleStatus(s.id),
                            }
                          : s.defaultChecked
                            ? { defaultChecked: s.defaultChecked }
                            : {})}
                      />
                    ))}
                  </div>
                ))}
              </FilterCategory>
            </div>

            <div>
              <FilterCategory
                title="Providers"
                count={String(providers.length)}
                {...(onResetProviders
                  ? { onReset: onResetProviders }
                  : onToggleProvider && hiddenProviders?.size
                    ? { onReset: () => hiddenProviders.forEach((id) => onToggleProvider(id)) }
                    : {})}
              >
                {providers.length === 0 ? (
                  <p className="px-1.5 py-1.5 text-[11px] text-[var(--ink-4)]">
                    No providers scheduled today.
                  </p>
                ) : (
                  providers.map((p) => (
                    <OptionsCheckRow
                      key={p.id}
                      label={p.name}
                      sublabel={p.role}
                      checked={!hiddenProviders?.has(p.id)}
                      onChange={() => onToggleProvider?.(p.id)}
                    />
                  ))
                )}
              </FilterCategory>

              <FilterCategory
                title="Operatories"
                count={String(operatoryRows.length)}
                onReset={() => onResetOperatories?.()}
              >
                {operatoryRows.length === 0 ? (
                  <p className="px-1.5 py-1.5 text-[11px] text-[var(--ink-4)]">
                    No operatories scheduled today.
                  </p>
                ) : (
                  operatoryRows.map((op) => (
                    <OptionsCheckRow
                      key={op.id}
                      label={op.name}
                      {...(op.role ? { sublabel: op.role } : {})}
                      {...(onToggleOperatory
                        ? {
                            checked: !hiddenOperatories?.has(op.id),
                            onChange: () => onToggleOperatory(op.id),
                          }
                        : {})}
                    />
                  ))
                )}
              </FilterCategory>

              <FilterCategory
                title="Flags"
                count={String(flagCount)}
                onReset={() => onResetFlags?.()}
              >
                {DAY_FLAG_GROUPS.map((group, gi) => (
                  <div key={group.label} className={gi > 0 ? 'mt-2' : undefined}>
                    <FlagGroupLabel color={group.labelColor}>{group.label}</FlagGroupLabel>
                    {group.items.map((item) => (
                      <OptionsCheckRow
                        key={item.id}
                        label={item.label}
                        {...(item.icon ? { icon: item.icon } : {})}
                        {...(item.iconColor ? { iconColor: item.iconColor } : {})}
                        {...(onToggleFlag
                          ? {
                              checked: selectedFlags?.has(item.id) ?? false,
                              onChange: () => onToggleFlag(item.id),
                            }
                          : item.defaultChecked
                            ? { defaultChecked: item.defaultChecked }
                            : {})}
                      />
                    ))}
                  </div>
                ))}
              </FilterCategory>

              <OptionsSection title="Production threshold" hint="Show only appointments ≥ $">
                <input
                  type="range"
                  min={0}
                  max={5000}
                  step={50}
                  value={productionThreshold}
                  onChange={(e) => onProductionThresholdChange(Number(e.target.value))}
                  className="cal-range w-full accent-[var(--success)]"
                />
                <div className="mt-1 flex justify-between font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">
                  <span>$0</span>
                  <span className="font-semibold text-[var(--ink-0)]">
                    ${productionThreshold.toLocaleString()}
                    {productionThreshold === 0 ? ' (all)' : '+'}
                  </span>
                  <span>$5,000+</span>
                </div>
              </OptionsSection>

              <OptionsDivider />

              <p className={cn(eyebrowClass, 'mb-2.5 tracking-[0.12em] text-[var(--ink-4)]')}>
                Display preferences · set once
              </p>

              <OptionsSection title="Time density" hint="Controls how tall each hour block is">
                <ToggleRow
                  options={['Small', 'Medium', 'Large']}
                  value={timeDensity}
                  onChange={(v) => onTimeDensityChange(v as CalendarTimeDensity)}
                />
                <p className="mt-1.5 font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">
                  {timeDensityHint}
                </p>
              </OptionsSection>

              <OptionsSection title="Cell detail" hint="How much info shows in each appointment">
                <ToggleRow
                  options={['Compact', 'Large']}
                  value={large ? 'Large' : 'Compact'}
                  onChange={(v) => onLargeChange(v === 'Large')}
                />
              </OptionsSection>
            </div>
          </div>
        </div>

        <div className="flex gap-2 border-t border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-3">
          <Button variant="secondary" className="flex-1" onClick={onReset}>
            Reset all filters
          </Button>
          <Button variant="primary" className="flex-1" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </ViewportDrawer>
  );
}

function OptionsSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <p className={cn(eyebrowClass, 'mb-2')}>{title}</p>
      {children}
      {hint ? (
        <p className="mt-1.5 text-[11px] tracking-[-0.005em] text-[var(--ink-4)]">{hint}</p>
      ) : null}
    </div>
  );
}

function OptionsDivider() {
  return <div className="my-3.5 h-px bg-[var(--cream-2)]" />;
}

function ToggleRow({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="inline-flex w-full gap-0 rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-1)] p-0.5">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={cn(
            'flex-1 rounded-[7px] border-0 px-3.5 py-1.5 text-[12.5px] font-medium tracking-[-0.005em] transition-[background,color,box-shadow]',
            value === option
              ? 'bg-[var(--cream-0)] font-semibold text-[var(--ink-0)] shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
              : 'bg-transparent text-[var(--ink-3)] hover:text-[var(--ink-0)]',
          )}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function FilterCategory({
  title,
  count,
  onReset,
  children,
}: {
  title: string;
  count: string;
  onReset?: () => void;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="mb-4 overflow-hidden rounded-[10px] border border-[var(--cream-2)] bg-[var(--cream-0)]">
      <div
        role="button"
        tabIndex={0}
        className={cn(
          'flex w-full cursor-pointer items-center gap-1.5 px-3 py-2.5',
          expanded ? 'border-b border-[var(--cream-2)]' : 'bg-[var(--cream-1)]',
        )}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
      >
        <span className="text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
          {title}
        </span>
        <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">({count})</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className="h-[22px] rounded-md px-1.5 text-[11px] text-[var(--ink-3)] hover:bg-[var(--cream-1)] hover:text-[var(--ink-0)]"
            onClick={(e) => {
              e.stopPropagation();
              onReset?.();
            }}
          >
            Reset
          </button>
          <Icon
            kind="down"
            size={14}
            className={cn('text-[var(--ink-4)] transition-transform', expanded && 'rotate-180')}
          />
        </div>
      </div>
      {expanded ? <div className="px-2.5 py-2">{children}</div> : null}
    </div>
  );
}

function OptionsCheckRow({
  label,
  sublabel,
  defaultChecked,
  dot,
  icon,
  iconColor,
  checked: checkedProp,
  onChange,
}: {
  label: string;
  sublabel?: string;
  defaultChecked?: boolean;
  dot?: string;
  icon?: DayOptionsCheckItem['icon'];
  iconColor?: string;
  /** Controlled mode — when `onChange` is provided, `checked` drives the state. */
  checked?: boolean;
  onChange?: () => void;
}) {
  const [localChecked, setLocalChecked] = useState(!!defaultChecked);
  const controlled = onChange != null;
  const checked = controlled ? !!checkedProp : localChecked;
  const toggle = controlled ? onChange : () => setLocalChecked((v) => !v);

  return (
    <button
      type="button"
      onClick={toggle}
      className="flex w-full cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-[var(--cream-1)]"
    >
      <span
        className={cn(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border-[1.5px]',
          checked
            ? 'border-[var(--sage-0)] bg-[var(--sage-0)] text-[var(--cream-0)]'
            : 'border-[var(--cream-shadow)] bg-[var(--cream-0)]',
        )}
      >
        {checked ? <Icon kind="check" size={10} stroke={3} color="currentColor" /> : null}
      </span>
      {dot ? <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: dot }} /> : null}
      {icon ? <Icon kind={icon} size={12} color={iconColor ?? 'var(--ink-2)'} /> : null}
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] tracking-[-0.005em] text-[var(--ink-0)]">{label}</div>
        {sublabel ? (
          <div className="mt-px text-[10.5px] text-[var(--ink-4)]">{sublabel}</div>
        ) : null}
      </div>
    </button>
  );
}

function QuickFilterChip({
  children,
  active,
  onClick,
  color,
  tint,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  color: string;
  tint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[12px] font-semibold tracking-[-0.005em]',
        !active && 'border-[var(--cream-2)] bg-[var(--cream-0)] text-[var(--ink-2)]',
      )}
      style={active ? { color, borderColor: color, background: tint } : undefined}
    >
      <span className="h-[7px] w-[7px] rounded-full" style={{ background: color }} />
      {children}
    </button>
  );
}

function FlagGroupLabel({ children, color }: { children: ReactNode; color: string }) {
  return (
    <p
      className="px-1 pb-1 text-[9px] font-semibold uppercase tracking-[0.1em]"
      style={{ color }}
    >
      {children}
    </p>
  );
}
